import { ref } from 'vue'
import { db } from '@/firebase/firebase'
import {
  collection,
  doc,
  addDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  getDocs,
  increment
} from 'firebase/firestore'

export function usePropertyComment() {
  const loading = ref(false)
  const error = ref(null)

  // 이메일 마스킹 (kim****@naver.com)
  const maskEmail = (email) => {
    if (!email) return '익명'
    const [local, domain] = email.split('@')
    if (!domain) return '익명'

    const visiblePart = local.slice(0, 3)
    return `${visiblePart}****@${domain}`
  }

  // 댓글 목록 조회 (부모 댓글만)
  const getComments = async (propertyId) => {
    loading.value = true
    error.value = null

    try {
      const q = query(
        collection(db, 'propertyComments'),
        where('propertyId', '==', propertyId),
        where('parentId', '==', null),
        orderBy('createdAt', 'desc')
      )

      const snapshot = await getDocs(q)
      const comments = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        maskedEmail: maskEmail(doc.data().userEmail)
      }))

      return comments
    } catch (err) {
      console.error('댓글 조회 실패:', err)
      error.value = err.message
      return []
    } finally {
      loading.value = false
    }
  }

  // 대댓글 목록 조회
  const getReplies = async (parentId) => {
    try {
      const q = query(
        collection(db, 'propertyComments'),
        where('parentId', '==', parentId),
        orderBy('createdAt', 'asc')
      )

      const snapshot = await getDocs(q)
      return snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        maskedEmail: maskEmail(doc.data().userEmail)
      }))
    } catch (err) {
      console.error('대댓글 조회 실패:', err)
      return []
    }
  }

  // 댓글 작성
  const createComment = async (commentData) => {
    loading.value = true
    error.value = null

    try {
      const docRef = await addDoc(collection(db, 'propertyComments'), {
        propertyId: commentData.propertyId,
        userId: commentData.userId,
        userEmail: commentData.userEmail,
        content: commentData.content,
        parentId: commentData.parentId || null,
        replyCount: 0,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      })

      // 부모 댓글이 있으면 replyCount 증가
      if (commentData.parentId) {
        await updateDoc(doc(db, 'propertyComments', commentData.parentId), {
          replyCount: increment(1),
          updatedAt: new Date().toISOString()
        })
      }

      return { success: true, id: docRef.id }
    } catch (err) {
      console.error('댓글 작성 실패:', err)
      error.value = err.message
      return { success: false, error: err.message }
    } finally {
      loading.value = false
    }
  }

  // 댓글 수정
  const updateComment = async (commentId, content) => {
    loading.value = true
    error.value = null

    try {
      await updateDoc(doc(db, 'propertyComments', commentId), {
        content,
        updatedAt: new Date().toISOString()
      })

      return { success: true }
    } catch (err) {
      console.error('댓글 수정 실패:', err)
      error.value = err.message
      return { success: false, error: err.message }
    } finally {
      loading.value = false
    }
  }

  // 댓글 삭제
  const deleteComment = async (commentId, parentId = null) => {
    loading.value = true
    error.value = null

    try {
      // 대댓글인 경우 부모의 replyCount 감소
      if (parentId) {
        await updateDoc(doc(db, 'propertyComments', parentId), {
          replyCount: increment(-1),
          updatedAt: new Date().toISOString()
        })
      } else {
        // 부모 댓글 삭제 시 대댓글도 모두 삭제
        const repliesQuery = query(
          collection(db, 'propertyComments'),
          where('parentId', '==', commentId)
        )
        const repliesSnapshot = await getDocs(repliesQuery)

        for (const replyDoc of repliesSnapshot.docs) {
          await deleteDoc(doc(db, 'propertyComments', replyDoc.id))
        }
      }

      await deleteDoc(doc(db, 'propertyComments', commentId))

      return { success: true }
    } catch (err) {
      console.error('댓글 삭제 실패:', err)
      error.value = err.message
      return { success: false, error: err.message }
    } finally {
      loading.value = false
    }
  }

  // 댓글 수 조회
  const getCommentCount = async (propertyId) => {
    try {
      const q = query(
        collection(db, 'propertyComments'),
        where('propertyId', '==', propertyId)
      )

      const snapshot = await getDocs(q)
      return snapshot.size
    } catch (err) {
      console.error('댓글 수 조회 실패:', err)
      return 0
    }
  }

  return {
    loading,
    error,
    maskEmail,
    getComments,
    getReplies,
    createComment,
    updateComment,
    deleteComment,
    getCommentCount
  }
}
